import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";

import Table from "../../components/Tables/Table";
import {
  Divider,
  Drawer,
  Form,
  Modal,
  Select,
  Space,
  Tag,
  Tooltip,
} from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";
import CustomDropDownInput from "../../components/Inputs/CustomDropDownInput";
import { getInstitutions } from "../../services/institutionService";
import TextArea from "../../components/Inputs/TextAreaInput";
import {
  createResearchCentre,
  deleteResearchCentre,
  getResearchCentres,
  updateResearchCentre,
} from "../../services/researchCentreService";
import { researchCentreSchema } from "../../validators/researchCentreValidators";
import { getResearchAreas } from "../../services/researchAreaService";
import MultiSelectDropdown from "../../components/Inputs/MultiSelectDropDown";
import { BiShow } from "react-icons/bi";
import { useStateContext } from "../../context/ContextProvider";
import { isInstitutionAdmin } from "../../utils/authHelpers";

const ResearchCentre = () => {
  const [researchCentres, setResearchCentres] = useState([]);
  const [filteredResearchCentres, setFilteredResearchCentres] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [researchCentreToEdit, setResearchCentreToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [researchCentreToDelete, setResearchCentreToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [researchAreas, setResearchAreas] = useState([]);
  const [selectedInstitution, setSelectedInstitution] =
    useState("Not Selected");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedResearchCentre, setSelectedResearchCentre] = useState(null);

  const { user } = useStateContext();
  const isInstAdmin = isInstitutionAdmin(user);

  useEffect(() => {
    loadResearchCentres();
    loadInstitutions();
    loadResearchAreas();
  }, []);

  useEffect(() => {
    let filtered = researchCentres;
    if (searchTerm.trim() !== "") {
      filtered = researchCentres.filter((item) =>
        item?.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedInstitution !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.institution?.id?.toString() === selectedInstitution?.toString()
      );
    }

    setFilteredResearchCentres(filtered);
  }, [searchTerm, researchCentres, selectedInstitution]);

  const loadResearchAreas = async () => {
    try {
      const { data } = await getResearchAreas();
      setResearchAreas(data.data);
    } catch (error) {
      toast.error("Failed to load research areas");
    }
  };

  const loadInstitutions = async () => {
    try {
      const { data } = await getInstitutions();
      setInstitutions(data.data);
    } catch (error) {
      toast.error("Failed to load institutions");
    }
  };

  const loadResearchCentres = async () => {
    setLoading(true);
    try {
      const { data } = await getResearchCentres();
      setResearchCentres(data.data);
      setFilteredResearchCentres(data.data);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Failed to load research centres"
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (researchCentreToEdit) {
      onUpdateResearchCentre(payload, resetForm);
    } else {
      onCreateResearchCentre(payload, resetForm);
    }
  };

  const onCreateResearchCentre = async (payload, resetForm) => {
    setErrs(null);

    try {
      const { data } = await createResearchCentre(payload);

      setResearchCentres((prev) => [...prev, data.data]);

      toast.success("Research centre added successfully");

      setIsModalOpen(false);

      resetForm();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response.data.error || "Research centre was not added, try again!"
        );
      }
    }
  };

  const handleEdit = (category) => {
    setResearchCentreToEdit(category);
    setIsModalOpen(true);
  };

  const onUpdateResearchCentre = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalResearchCentres = [...researchCentres];

      const researchCentreIndex = originalResearchCentres.findIndex(
        (researchCentre) => researchCentre.id === researchCentreToEdit.id
      );

      if (researchCentreIndex === -1)
        throw new Error("Research centres not found.");

      const { data } = await updateResearchCentre(
        researchCentreToEdit.id,
        payload
      );

      originalResearchCentres[researchCentreIndex] = data.data;

      setResearchCentres(originalResearchCentres);

      setResearchCentreToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Research centre updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Research centre not updated, try again!"
        );
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.name?.toUpperCase()}`
    );
    setResearchCentreToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeleteResearchCentre = async () => {
    const originalResearchCentres = [...researchCentres];

    const newResearchCentres = originalResearchCentres.filter(
      (researchCentre) => researchCentre.id !== researchCentreToDelete.id
    );

    setResearchCentres(newResearchCentres);

    try {
      await deleteResearchCentre(researchCentreToDelete?.id);

      toast.success("Research centre deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message ||
          error?.response?.data?.error ||
          "Research centre not deleted, try again later"
        }`
      );

      setResearchCentres(originalResearchCentres);
    } finally {
      setIsDeleteModalOpen(false);

      setResearchCentreToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setResearchCentreToEdit(null);
    setResearchCentreToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 70,
    },
    {
      title: "Research Centre",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => {
        const isEmpty = !text?.trim();
        const displayText = isEmpty ? "N/A" : text;

        return isEmpty ? (
          <div style={{ textAlign: "left", color: "#888" }}>{displayText}</div>
        ) : (
          <Tooltip title={text}>
            <div
              style={{
                maxWidth: 250,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {text}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Research Areas",
      dataIndex: "research_areas",
      key: "research_areas",
      width: 300,
      // allow wrapping in this cell
      onCell: () => ({
        style: {
          whiteSpace: "normal",
          wordWrap: "break-word",
        },
      }),
      render: (areas) =>
        areas && areas.length > 0 ? (
          <div
            className="flex flex-wrap gap-2"
            style={{ rowGap: 8, columnGap: 8 }}
          >
            {areas.map((area) => (
              <Tag
                key={area.id}
                color="blue"
                className="whitespace-normal break-words"
                style={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                }}
              >
                {area.name}
              </Tag>
            ))}
          </div>
        ) : (
          <span className="text-gray-400 italic">None</span>
        ),
    },
    ...(!isInstAdmin
      ? [
          {
            title: "Institution",
            dataIndex: ["institution", "name"],
            key: "institution",
            render: (text) => (
              <span className="text-gray-700 text-sm">{text || "N/A"}</span>
            ),
          },
        ]
      : []),
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
    },
    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="View Details">
            <button
              className="p-2 border border-blue-200 rounded-md text-blue-600 hover:bg-blue-50 transition"
              onClick={() => {
                setSelectedResearchCentre(record);
                setIsDrawerOpen(true);
              }}
            >
              <BiShow size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Edit Research Centre">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Research Centre">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading title="Research Centres" subtitle1="Research Centres" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Research Centres"
            count={filteredResearchCentres.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Research Centre"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredResearchCentres.map((item) => ({
              ...item,
              key: item.id,
            }))}
            extra={
              !isInstAdmin && (
                <div className="mb-2 py-2 flex gap-2">
                  <Select
                    multiple={false}
                    id="institutions"
                    defaultValue={selectedInstitution}
                    className="w-full h-[36px] min-w-[250px]"
                    onChange={(value) => {
                      setSelectedInstitution(value);
                    }}
                    showSearch={true}
                    filterOption={(input, option) =>
                      option?.children
                        ?.toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    <Select.Option value={"Not Selected"}>
                      Filter by Institution
                    </Select.Option>
                    {institutions &&
                      institutions.length > 0 &&
                      institutions.map((option) => (
                        <Select.Option key={option.id} value={option.id}>
                          {option.name}
                        </Select.Option>
                      ))}
                  </Select>
                </div>
              )
            }
          />

          {/* Modal */}

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {researchCentreToEdit
                    ? "Edit Research Centre"
                    : "Add Research Centre"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            <Formik
              initialValues={{
                name: researchCentreToEdit ? researchCentreToEdit?.name : "",
                location: researchCentreToEdit
                  ? researchCentreToEdit?.location
                  : "",
                description: researchCentreToEdit
                  ? researchCentreToEdit?.description
                  : "",
                institution_id: researchCentreToEdit
                  ? researchCentreToEdit?.institution?.id
                  : isInstAdmin
                  ? user?.institution?.id
                  : "",
                research_areas: researchCentreToEdit
                  ? researchCentreToEdit.research_areas?.map(
                      (area) => area.id
                    ) || []
                  : [],
              }}
              enableReinitialize
              validationSchema={researchCentreSchema}
              onSubmit={(values, { resetForm }) => {
                onSubmit(values, resetForm);
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                touched,
                setFieldValue,
                submitForm,
                setFieldTouched,
              }) => {
                return (
                  <Form className="space-y-5 px-4! py-4!">
                    {errs && <FormErrors errors={errs} />}

                    {isInstAdmin ? (
                      <input
                        type="hidden"
                        name="institution_id"
                        value={
                          researchCentreToEdit
                            ? researchCentreToEdit?.institution?.id
                            : user?.institution?.id
                        }
                      />
                    ) : (
                      <CustomDropDownInput
                        name="institution_id"
                        title="Institution"
                        placeholder="Select an institution"
                        requiredField
                        errors={errors}
                        values={values}
                        touched={touched}
                        displayLabel="name"
                        idLabel="id"
                        options={institutions}
                        setFieldValue={setFieldValue}
                      />
                    )}

                    <TextInput
                      name="name"
                      title="Research Centre"
                      placeholder="UVRI"
                      requiredField
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <TextInput
                      name="location"
                      title="Location"
                      placeholder="Entebbe"
                      requiredField
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <TextArea
                      name="description"
                      title="Description"
                      placeholder="Enter description"
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <MultiSelectDropdown
                      title="Research Areas"
                      name="research_areas"
                      placeholder="Select Research Areas"
                      errors={errors}
                      requiredField={true}
                      options={researchAreas}
                      values={values.research_areas || []}
                      displayLabel="name"
                      idLabel="id"
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      touched={touched}
                    />

                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteResearchCentre}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />

          <Drawer
            title={
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-800">
                  {selectedResearchCentre?.name || "Research Centre Details"}
                </h2>
              </div>
            }
            width={520}
            onClose={() => setIsDrawerOpen(false)}
            open={isDrawerOpen}
            placement="right"
          >
            {selectedResearchCentre ? (
              <div className="text-gray-700 text-sm space-y-6 px-1">
                <div className="grid grid-cols-1 gap-y-3">
                  <div>
                    <strong>Institution:</strong>{" "}
                    {selectedResearchCentre?.institution?.name || "N/A"}
                  </div>
                  <div>
                    <strong>Location:</strong>{" "}
                    {selectedResearchCentre?.location || "—"}
                  </div>
                  <div>
                    <strong>Description:</strong>{" "}
                    {selectedResearchCentre?.description || "—"}
                  </div>
                </div>

                <Divider />

                <div className="space-y-2">
                  <strong className="block">Research Areas:</strong>
                  {selectedResearchCentre?.research_areas?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {selectedResearchCentre.research_areas.map((area) => (
                        <Tag
                          color="blue"
                          key={area.id}
                          style={{
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          }}
                        >
                          {area.name}
                        </Tag>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">No research areas listed.</p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-gray-500">No research centre selected.</p>
            )}
          </Drawer>
        </div>
      </div>
    </>
  );
};

export default ResearchCentre;
